import React, { useState } from 'react';
import { FormDefinitionField, FormItem } from '@/interfaces/api/models';
import { FormFieldValue, SignaturePreview } from './form-helpers';
import FormFieldRenderer from './FormFieldRenderer';

export interface SignatureFieldContext {
    ensurePersisted: () => Promise<void>;
    itemID: string;
}

// The set of per-field callbacks `ManageFormModal` computes once and threads down through
// `FormSection`/`FormSectionInstance`. They are grouped in one interface so the three components
// that pass them through don't each re-declare five props.
export interface FormFieldWiring {
    // Fired whenever a field has been "committed" — on blur for controls that have one, and
    // directly from onChange for the controls that don't. Drives the autosave debounce.
    onFieldTouched?: () => void;
    // Resolves the Select/MultiSelect option list for a field, merging the field's own
    // `DataOption` CSV with any `FormDataOptions` override matched on `MasterID`.
    availableOptions?: (field: FormDefinitionField) => string[];
    onUpload?: (file: File) => Promise<string>;
    resolveImageSrc?: (attachmentID: string) => string;
    // A builder rather than a static object, because a signature context is per-item (it carries
    // the item's own ID), and the item is only in scope down here.
    buildSignatureContext?: (item: FormItem) => SignatureFieldContext;
    // Lazily resolves a completed signature's thumbnail image + "Signed by"/date, for the small
    // preview next to a signed field. Returns null while unresolved/pending — the field then falls
    // back to its plain "View Signature" text, same resilience style as `resolveImageSrc`.
    resolveSignaturePreview?: (signatureID: string) => SignaturePreview | null;
    // Called with the freshly-saved `Signature` record once a signature is captured or re-signed,
    // so the cache `resolveSignaturePreview` reads from can be updated immediately. A re-sign keeps
    // the *same* signature ID (only its Attachment changes), so without this the ID-keyed cache
    // would never refresh on its own — the preview would only catch up after a full page reload.
    onSignatureSaved?: (signature: any) => void;
}

// DataTypes whose controls have no natural blur event, so autosave has to be triggered from their
// onChange instead (ports the plan's `noBlurTypes` set, plus Image/Signature which likewise commit
// their value through a click rather than by leaving an input).
const NO_BLUR_TYPES = new Set(['Checkbox', 'Boolean', 'Select', 'MultiSelect', 'Table', 'Image', 'Signature']);

export interface FormFieldBindingProps extends FormFieldWiring {
    item: FormItem;
    field: FormDefinitionField;
    form: any;
    disabled?: boolean;
}

/**
 * Binds one `FormItem` to the `@mantine/form` store.
 *
 * The form runs in `mode: 'uncontrolled'`, so the authoritative value lives in the form's ref
 * store and writing to it does NOT re-render the modal — that is what keeps typing in a 100-field
 * form cheap. Every `Fields/*` component is a controlled component though (`value` + `onChange`),
 * and a controlled input whose parent never re-renders has its DOM value reset by React on every
 * keystroke. This binding closes that gap: it mirrors the value in its own local state, so a
 * keystroke re-renders exactly one field and nothing else.
 *
 * The caller keys this component on `form.key(item.ID)` so that external writes to the store
 * (`form.setValues` on load / draft restore / upgrade) re-seed the local state.
 */
function FormFieldBinding({ item, field, form, disabled, onFieldTouched, availableOptions, onUpload, resolveImageSrc, buildSignatureContext, resolveSignaturePreview, onSignatureSaved }: FormFieldBindingProps) {
    const [value, setValue] = useState<FormFieldValue>(() => form.getValues()[item.ID] ?? null);

    const dataType = field.DataType ?? '';
    const hasNoBlur = NO_BLUR_TYPES.has(dataType);

    const handleChange = (next: FormFieldValue) => {
        setValue(next);
        // `forceUpdate: false` keeps `form.key(item.ID)` stable, so the field is not remounted
        // (and does not lose focus) mid-edit.
        form.setFieldValue(item.ID, next, { forceUpdate: false });
        if (hasNoBlur) onFieldTouched?.();
    };

    return (
        // `data-form-item-id` gives `scrollToFirstError` (see `ManageFormModal`'s save/complete
        // paths) a DOM anchor per item — it is the same ID `buildValidator` keys top-level errors
        // by, so the first key of a failed `form.validate()` resolves straight back to the field
        // that needs it.
        <div data-form-item-id={item.ID}>
            <FormFieldRenderer
                field={field}
                value={value}
                onChange={handleChange}
                onBlur={hasNoBlur ? undefined : onFieldTouched}
                error={form.errors?.[item.ID]}
                disabled={disabled}
                sectionGroup={item.SectionGroup ?? 0}
                // A Table's cells are validated individually, under synthetic `getTableCellID` keys
                // rather than this item's own ID, so the table needs the whole error map to look its
                // own cells up in.
                cellErrors={form.errors}
                onClearCellError={form.clearFieldError}
                availableOptions={availableOptions?.(field)}
                resolveFieldOptions={availableOptions}
                onUpload={onUpload}
                resolveImageSrc={resolveImageSrc}
                buildSignatureContext={buildSignatureContext}
                resolveSignaturePreview={resolveSignaturePreview}
                onSignatureSaved={onSignatureSaved}
                signatureContext={dataType === 'Signature' ? buildSignatureContext?.(item) : undefined}
            />
        </div>
    );
}

export default React.memo(FormFieldBinding);
